import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";
import {
  Account,
  AccountInformation,
  AccountUpdateParams,
  DecodableApiError,
  ResourceView,
} from "decodable-sdk-js/v1alpha2";

import { useDecodableApiFetch } from "../../lib/api";
import { Plan } from "../constants";
import { useGtag } from "../../components/GTagProvider";
import { splitEmailList } from "../../lib/util";
import { CellId, CellManifest } from "../utils";
import { useAccountsApi } from "./api";
import { useActiveAccount } from "./useAccounts";
import { FileHolder } from "decodable-ui/lib/components/FileUpload";

export function useResolveHost(
  opts: UseMutationOptions<boolean, Error, { host: string }> = {}
) {
  return useMutation(
    ["data-plane-host"],
    async ({ host }) => {
      if (!host) {
        return false;
      }
      var protocol =
        host === "localhost" || host.startsWith("localhost:")
          ? "http"
          : "https";
      await fetch(`${protocol}://${host}`, {
        method: "HEAD",
        mode: "no-cors",
      });

      return true;
    },
    {
      ...opts,
      retry: 3,
      retryDelay: 500,
    }
  );
}

export function useListUserAccounts(
  params: {
    pageSize?: number;
    pageToken?: string;
    view?: ResourceView;
  },
  options?: UseQueryOptions<
    { next_page_token?: string; items: Account[] },
    Error
  >
) {
  const accountsApi = useAccountsApi();
  return useQuery(
    ["accounts"],
    () => {
      return accountsApi.listUserAccounts(params);
    },
    options
  );
}

export function useGetAccountMetadata() {
  const accountsApi = useAccountsApi();
  return useQuery(["accounts", "metadata"], () => {
    return accountsApi.getAccountMetadata();
  });
}

export function useGetAccountInformation(id: string) {
  const accountsApi = useAccountsApi();
  return useQuery(["accounts", "information"], () => {
    return accountsApi.getAccountInformation({ id });
  });
}

export function useUpdateAccount(
  id: string,
  options?: UseMutationOptions<
    Account & AccountInformation,
    Error,
    AccountUpdateParams
  >
) {
  const accountsApi = useAccountsApi();
  const queryClient = useQueryClient();

  return useMutation(
    (accountUpdateParams) =>
      accountsApi.updateAccount({ id, ...accountUpdateParams }),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(["accounts"]);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
}

export function useAvailableCells(
  opts?: UseQueryOptions<CellManifest, DecodableApiError | Error>
) {
  const decodableApiFetch = useDecodableApiFetch();

  return useQuery<CellManifest, DecodableApiError | Error>(
    ["cells", "available"],
    () => decodableApiFetch("/v1alpha2/cells/available"),
    opts
  );
}

export function useCreateAccount() {
  const gtag = useGtag();
  const decodableApiFetch = useDecodableApiFetch();

  return useMutation(
    async ({
      cell,
      plan,
      name,
      displayName,
      invites,
    }: {
      cell: CellId;
      plan: Plan;
      name: string;
      displayName: string;
      invites: string;
    }) => {
      const body = {
        name: name,
        display_name: displayName,
        subscription: {
          plan_id: plan.id,
        },
        invite_emails: undefined,
      };

      if (invites) {
        body.invite_emails = splitEmailList(invites);
      }

      try {
        await decodableApiFetch(
          "/v1alpha2/accounts",
          {
            body: JSON.stringify(body),
            headers: { "Content-Type": "application/json" },
          },
          { targetCell: cell }
        );
      } catch (ex) {
        if (typeof jest === "undefined") {
          console.error(ex);
        }

        throw ex;
      }

      gtag({
        event: "account_created",
        account_name: name,
        plan: plan.id,
      });
    }
  );
}

export const useUploadCertificateSigningRequest = (
  files: FileHolder,
  opts: UseQueryOptions<void, DecodableApiError>
) => {
  const { activeAccount } = useActiveAccount();
  const accountService = useAccountsApi();
  const csr = Array.from(files.values()).pop();

  return useQuery(
    ["accounts", activeAccount.name, "certificate_signing_request"],
    async () =>
      accountService.uploadCertificateSigningRequest(activeAccount.name, {
        csr,
      }),
    opts
  );
};

export const useDownloadSignedCertificate = (
  opts: UseMutationOptions<{ blob: Blob; filename: string }, DecodableApiError>
) => {
  const { activeAccount } = useActiveAccount();
  const accountService = useAccountsApi();

  return useMutation(
    ["accounts", activeAccount.name, "certificate"],
    async () => await accountService.getSignedCertificate(activeAccount.name),
    opts
  );
};
